
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun, MessageCircle, Play, User, BookOpen, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTheme } from '@/hooks/useTheme';
import { motion, AnimatePresence } from 'framer-motion';
import { useTour } from '@/hooks/useTour';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'; // Import Dialog components
import { AskWidget } from '@/components/search/AskWidget'; // Import AskWidget

const navLinks = [
  { name: 'Início', path: '/' },
  { name: 'Palestras', path: '/palestras' },
  { name: 'Sobre', path: '/about' },
  { name: 'Projetos', path: '/projects' },
  // { name: 'Blog', path: '/blog' },
  { name: 'Mentoria', path: '/mentoria' },
  { name: 'Mídia', path: '/media' },
  { name: 'Contato', path: '/contact' },
];

interface NavbarProps {
  onToggleChat?: () => void;
  isChatOpen?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ onToggleChat, isChatOpen = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAskWidgetOpen, setIsAskWidgetOpen] = useState(false); // State for AskWidget modal
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isPulsePage = true; // site inteiro usa PULSE
  const { theme, toggleTheme } = useTheme();
  const { startTour } = useTour();
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <motion.header
      className={cn(
        'fixed w-full top-0 z-[60] transition-all duration-300',
        isPulsePage
          ? scrolled
            ? 'backdrop-blur-sm shadow-sm'
            : 'bg-transparent'
          : scrolled
            ? 'bg-white/90 dark:bg-portfolio-dark/90 backdrop-blur-sm shadow-sm'
            : 'bg-transparent'
      )}
      style={isPulsePage && scrolled ? { backgroundColor: 'rgba(11,11,11,0.92)' } : undefined}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <div className="container mx-auto px-4 py-4 md:py-5 flex items-center justify-between">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Link
            to="/"
            className="font-heading font-bold text-xl md:text-2xl group flex items-center gap-2"
            onClick={closeMenu}
          >
            <motion.img
              src="/images/logo/logo-jv-tree.png"
              alt="Johnny Vaz - Logo"
              className="w-8 h-8 md:w-10 md:h-10"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ duration: 0.2 }}
            />
            <motion.span
              className={isPulsePage ? undefined : 'gradient-text'}
              style={isPulsePage ? { color: '#F2EDE3', fontFamily: "'JetBrains Mono', monospace", fontSize: '0.95rem', letterSpacing: '0.04em', textTransform: 'uppercase' } : undefined}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              Johnny Vaz
            </motion.span>
          </Link>
        </motion.div>

        {/* Desktop Navigation */}
        <motion.nav 
          className="hidden md:flex items-center space-x-8"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <ul className="flex items-center space-x-8">
            {navLinks.map((link, index) => (
              <li key={link.name}>
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 * index }}
                >
                  <Link
                    id={link.path === '/media' ? 'media-page-link' : undefined}
                    to={link.path}
                    className={cn(
                      'text-sm font-medium transition-colors relative px-1 py-2 group',
                      isPulsePage
                        ? location.pathname === link.path
                          ? 'text-white'
                          : 'text-[#8C8377] hover:text-[#F2EDE3]'
                        : cn(
                            'hover:text-portfolio-primary',
                            location.pathname === link.path
                              ? 'text-portfolio-primary'
                              : 'text-foreground'
                          )
                    )}
                    style={isPulsePage ? { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase' } : undefined}
                  >
                    {link.name}
                    <motion.div
                      className="absolute left-0 bottom-0 h-0.5"
                      style={{ backgroundColor: isPulsePage ? '#FF4500' : '#2563EB' }}
                      initial={{ width: location.pathname === link.path ? '100%' : '0%' }}
                      animate={{ width: location.pathname === link.path ? '100%' : '0%' }}
                      whileHover={{ width: '100%' }}
                      transition={{ duration: 0.3 }}
                    />
                  </Link>
                </motion.div>
              </li>
            ))}
          </ul>
          
          {/* Builder CTA — lançamento */}
          {/* <Link
            to="/builder"
            className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium tracking-wider uppercase transition-all hover:opacity-80"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              backgroundColor: '#FF4500',
              color: '#0B0B0B',
              letterSpacing: '0.08em',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Builder →
          </Link> */}

          <div className="flex items-center gap-2">
            {/* Auth Links */}
            {/* {user ? (
              <>
                <Link to="/dashboard">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2"
                  >
                    <User size={16} />
                    Dashboard
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    await signOut();
                    window.location.href = '/';
                  }}
                >
                  Sair
                </Button>
              </>
            ) : (
              <Link to="/auth/login">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  style={isPulsePage ? { color: '#8C8377' } : undefined}
                >
                  <User size={16} />
                  Entrar
                </Button>
              </Link>
            )} */}

            {/* Ask AI Button
            <Dialog open={isAskWidgetOpen} onOpenChange={setIsAskWidgetOpen}>
              <DialogTrigger asChild>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  role="button"
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Perguntar ao AI"
                  >
                    <Search size={20} />
                  </Button>
                </motion.div>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <AskWidget onClose={() => setIsAskWidgetOpen(false)} />
              </DialogContent>
            </Dialog>
             */}
            {/* Tour Button */}
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              role="button"
            >
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                aria-label="Iniciar Tour"
                className="relative overflow-hidden"
              >
                <Play size={20} />
              </Button>
            </motion.div>

            {/* Chat Button */}
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              role="button"
            >
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleChat}
                aria-label="Toggle chat"
                className={cn(
                  "relative overflow-hidden",
                  isChatOpen && "bg-portfolio-primary/10 text-portfolio-primary"
                )}
              >
                <motion.div
                  animate={isChatOpen ? { rotate: [0, 10, -10, 0] } : { rotate: 0 }}
                  transition={{ duration: 0.5, repeat: isChatOpen ? Infinity : 0, repeatDelay: 2 }}
                >
                  <MessageCircle size={20} />
                </motion.div>
                
                {/* Notification dot */}
                <motion.div
                  className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"
                  initial={{ scale: 0 }}
                  animate={{ scale: isChatOpen ? 0 : 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    className="w-full h-full bg-red-500 rounded-full"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.div>
              </Button>
            </motion.div>
            
                        {/* Theme Button */}
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          role="button"
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            aria-label="Toggle theme"
                            className="relative overflow-hidden"
                          >
                            <AnimatePresence mode="wait">
                              <motion.div
                                key={theme}
                                initial={{ rotate: -180, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: 180, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                              </motion.div>
                            </AnimatePresence>
                          </Button>
                        </motion.div>          </div>
        </motion.nav>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-2">
          {/* Mobile Ask AI Button */}
          <Dialog open={isAskWidgetOpen} onOpenChange={setIsAskWidgetOpen}>
            <DialogTrigger asChild>
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} role="button">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Perguntar ao AI"
                >
                  <Search size={20} />
                </Button>
              </motion.div>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <AskWidget onClose={() => setIsAskWidgetOpen(false)} />
            </DialogContent>
          </Dialog>

          {/* Mobile Chat Button */}
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} role="button">
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleChat}
              aria-label="Toggle chat"
              className={cn(
                "relative overflow-hidden",
                isChatOpen && "bg-portfolio-primary/10 text-portfolio-primary"
              )}
            >
              <MessageCircle size={20} />
              {!isChatOpen && (
                <motion.div
                  className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              )}
            </Button>
          </motion.div>
          
          {/* Mobile Theme Button */}
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} role="button">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={theme}
                  initial={{ rotate: -180, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 180, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                </motion.div>
              </AnimatePresence>
            </Button>
          </motion.div>
          
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} role="button">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={startTour}
              aria-label="Iniciar Tour"
            >
              <Play size={20} />
            </Button>
          </motion.div>
          
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} role="button">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isMenuOpen ? 'close' : 'open'}
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 0 }}
                  exit={{ rotate: 180 }}
                  transition={{ duration: 0.2 }}
                >
                  {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </motion.div>
              </AnimatePresence>
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.nav 
            className="md:hidden fixed inset-0 bg-background/95 backdrop-blur-sm z-[55] pt-20"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <motion.ul 
              className="flex flex-col items-center space-y-6 p-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              {navLinks.map((link, index) => (
                <motion.li 
                  key={link.name} 
                  className="w-full"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + (index * 0.1) }}
                >
                  <Link 
                    to={link.path}
                    className={cn(
                      'text-lg font-medium block py-4 px-6 text-center w-full rounded-md transition-all duration-200',
                      location.pathname === link.path 
                        ? 'bg-portfolio-primary/10 text-portfolio-primary scale-105'
                        : 'hover:bg-muted hover:scale-105'
                    )}
                    onClick={closeMenu}
                  >
                    {link.name}
                  </Link>
                </motion.li>
              ))}
              
              {/* Mobile Auth Links */}
              <motion.div 
                className="w-full px-6 pt-6 border-t"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                {user ? (
                  <div className="space-y-3">
                    <Link 
                      to="/dashboard"
                      onClick={closeMenu}
                      className="flex items-center justify-center gap-2 py-3 px-4 bg-portfolio-primary/10 text-portfolio-primary rounded-md"
                    >
                      <User size={20} />
                      Dashboard
                    </Link>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        await signOut();
                        closeMenu();
                        window.location.href = '/';
                      }}
                      className="w-full"
                    >
                      Sair
                    </Button>
                  </div>
                ) : (
                  <Link 
                    to="/auth/login"
                    onClick={closeMenu}
                    className="flex items-center justify-center gap-2 py-3 px-4 bg-portfolio-primary text-white rounded-md"
                  >
                    <User size={20} />
                    Entrar
                  </Link>
                )}
              </motion.div>
            </motion.ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
